import { motion, AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'

interface CelebrationAnimationProps {
	isVisible: boolean
	onComplete?: () => void
}

export function CelebrationAnimation({
	isVisible,
	onComplete,
}: CelebrationAnimationProps) {
	useEffect(() => {
		if (isVisible && onComplete) {
			// Call onComplete after animation duration
			const timer = setTimeout(onComplete, 3000)
			return () => clearTimeout(timer)
		}
	}, [isVisible, onComplete])

	const emojis = ['🎉', '✨', '🎊', '🌟', '💫', '🎯', '🚀', '💪']

	return (
		<AnimatePresence>
			{isVisible && (
				<motion.div
					className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
				>
					{/* Central success message */}
					<motion.div
						className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform"
						initial={{ scale: 0, rotate: -180 }}
						animate={{ scale: 1, rotate: 0 }}
						exit={{ scale: 0, rotate: 180 }}
						transition={{ type: 'spring', duration: 0.6 }}
					>
						<div className="rounded-full bg-green-500 px-8 py-4 text-white shadow-2xl">
							<motion.p
								className="text-2xl font-bold"
								animate={{ scale: [1, 1.1, 1] }}
								transition={{ duration: 0.6, repeat: 2 }}
							>
								Gelukt! 🎉
							</motion.p>
						</div>
					</motion.div>

					{/* Floating emojis */}
					{emojis.map((emoji, index) => (
						<motion.div
							key={index}
							className="absolute text-4xl"
							initial={{
								left: `${Math.random() * 100}%`,
								bottom: -50,
							}}
							animate={{
								y: [-window.innerHeight - 100],
								x: [0, (Math.random() - 0.5) * 200],
								rotate: [0, Math.random() * 360],
							}}
							transition={{
								duration: 3 + Math.random() * 2,
								delay: index * 0.2,
								ease: 'easeOut',
							}}
						>
							{emoji}
						</motion.div>
					))}

					{/* Confetti particles */}
					{Array.from({ length: 20 }).map((_, index) => (
						<motion.div
							key={`confetti-${index}`}
							className="absolute h-4 w-2"
							style={{
								backgroundColor: [
									'#f43f5e',
									'#10b981',
									'#3b82f6',
									'#f59e0b',
									'#8b5cf6',
								][Math.floor(Math.random() * 5)],
								left: `${Math.random() * 100}%`,
								top: -20,
							}}
							animate={{
								y: window.innerHeight + 100,
								x: (Math.random() - 0.5) * 100,
								rotate: Math.random() * 720,
							}}
							transition={{
								duration: 2 + Math.random(),
								delay: Math.random() * 0.5,
								ease: 'linear',
							}}
						/>
					))}
				</motion.div>
			)}
		</AnimatePresence>
	)
}
